// Moved from `src/hooks/mutation/useMutateUnassignClinicalTask.ts` (now a pure
// shim) with the D2 family (ARCFE-945).
//
// TOAST: PORT -> META (ARCFE-1082) — same severance, same two disclosed deltas
// (cache-level handler runs before `options.onError`; `meta` survives
// dehydration where a callback did not) as its assign sibling; the full
// measurement lives in that file's header. Both react-intl ids move VERBATIM —
// see `clinicalTaskToasts.messages.ts`.
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';

import type {
  ClinicalTaskResponse,
  GetClinicalTasksResponse,
} from '../../contracts/types';
import { unassignClinicalTask } from '../api/clinical-tasks';
import {
  cancelClinicalTaskQueries,
  invalidateClinicalTaskQueries,
  snapshotClinicalTaskQueries,
} from './clinicalTaskQueryCache';
import { CLINICAL_TASK_TOASTS } from './clinicalTaskToasts.messages';

type UnassignMutationContext = {
  previousSnapshots: [QueryKey, GetClinicalTasksResponse | undefined][];
};

export const useMutateUnassignClinicalTask = () => {
  const queryClient = useQueryClient();

  return useMutation<ClinicalTaskResponse, AxiosError, string, UnassignMutationContext>({
    mutationFn: async (taskId) => {
      const { data } = await unassignClinicalTask(taskId);
      return data;
    },
    onMutate: async (taskId) => {
      await cancelClinicalTaskQueries(queryClient);

      const previousSnapshots = snapshotClinicalTaskQueries(queryClient);

      const applyUpdate = (old: GetClinicalTasksResponse | undefined) => {
        if (!old?.tasks) return old;
        return {
          ...old,
          tasks: old.tasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  assignee_id: null,
                  assignee: null,
                  assignee_name: null,
                  assignee_avatar_url: null,
                }
              : task
          ),
        };
      };

      for (const [queryKey] of previousSnapshots) {
        queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, applyUpdate);
      }

      return { previousSnapshots };
    },
    onSettled: () => invalidateClinicalTaskQueries(queryClient),
    onError: (error, _taskId, context) => {
      if (context?.previousSnapshots) {
        for (const [key, data] of context.previousSnapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      logger.error('Error unassigning clinical task:', error.response?.data);
    },
    meta: CLINICAL_TASK_TOASTS.unassign,
  });
};
